import {useEffect, useRef} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialMediaAccountId} from '@Components/social-media/post.vo';
import {updateAccountIdsToPublishTo} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {useAppDispatch} from '@/hooks';
import type {AccountNeedsResizeMap, SocialPlatform} from '@Components/social-media/social-media.types';

const MAX_AUTO_SELECT_NEW_CONNECTED_ACCOUNTS = 1;

interface UseAccountPreselectionOpts {
  accounts: SocialAccount[];
  availableAccounts: SocialAccount[];
  accountIdsToPublishTo: SocialMediaAccountId[];
  accountNeedsResizeMapForDesigns: AccountNeedsResizeMap;
  emphasizedPlatform: SocialPlatform | undefined;
  isListLoaded: boolean;
  setIsListLoaded: (loaded: boolean) => void;
  isAccountPublishable: (accountId: number) => boolean;
  onAccountSelected: (idAccount: number) => void;
}

export function useAccountPreselection({
  accounts,
  availableAccounts,
  accountIdsToPublishTo,
  accountNeedsResizeMapForDesigns,
  emphasizedPlatform,
  isListLoaded,
  setIsListLoaded,
  isAccountPublishable,
  onAccountSelected,
}: UseAccountPreselectionOpts): void {
  const dispatch = useAppDispatch();
  const prevAccountsRef = useRef<SocialAccount[] | undefined>(undefined);

  const hasResizeMapBeenInitialized = (): boolean => {
    return Object.keys(accountNeedsResizeMapForDesigns).length !== 0;
  };

  const shouldPreselectSpecificPlatform = (): boolean => {
    return !!emphasizedPlatform;
  };

  const canPreferredAccountsBeSet = (): boolean => {
    return !isListLoaded && accounts.length > 0 && accountIdsToPublishTo.length === 0 && hasResizeMapBeenInitialized();
  };

  const getAccountIdsForEmphasizedPlatform = (): SocialMediaAccountId[] => {
    const accountIdsToSelect: SocialMediaAccountId[] = [];

    availableAccounts.forEach((account) => {
      if (account.type === emphasizedPlatform && isAccountPublishable(account.id)) {
        accountIdsToSelect.push(account.id);
      }
    });

    return accountIdsToSelect;
  };

  const getPreferredAccountIds = (): SocialMediaAccountId[] => {
    const preferredAccountIds: SocialMediaAccountId[] = [];

    availableAccounts.forEach((account) => {
      if (account.isPreferred && isAccountPublishable(account.id)) {
        preferredAccountIds.push(account.id);
      }
    });

    return preferredAccountIds;
  };

  const getValidatedAccountIds = (): SocialMediaAccountId[] => {
    const validatedAccountIds: SocialMediaAccountId[] = [];

    accountIdsToPublishTo.forEach((accountId) => {
      if (isAccountPublishable(accountId)) {
        validatedAccountIds.push(accountId);
      }
    });

    return validatedAccountIds;
  };

  const selectNewConnectedAccount = (previousAccounts: SocialAccount[], currentAccounts: SocialAccount[]): void => {
    if (currentAccounts.length - previousAccounts.length > MAX_AUTO_SELECT_NEW_CONNECTED_ACCOUNTS) {
      return;
    }

    const previousIds = previousAccounts.map((account) => {
      return account.id;
    });
    const currentIds = currentAccounts.map((account) => {
      return account.id;
    });

    const newAccountId = currentIds.filter((id) => {
      return !previousIds.includes(id);
    });

    const publishableAccounts = newAccountId.filter((accountId) => {
      return isAccountPublishable(accountId);
    });

    publishableAccounts.forEach((accountId) => {
      onAccountSelected(accountId);
    });
  };

  useEffect(() => {
    if (shouldPreselectSpecificPlatform()) {
      dispatch(updateAccountIdsToPublishTo(getAccountIdsForEmphasizedPlatform()));
    } else if (canPreferredAccountsBeSet()) {
      dispatch(updateAccountIdsToPublishTo(getPreferredAccountIds()));
      setIsListLoaded(true);
    } else {
      dispatch(updateAccountIdsToPublishTo(getValidatedAccountIds()));
    }
  }, [accounts, accountNeedsResizeMapForDesigns]);

  useEffect(() => {
    if (prevAccountsRef.current) {
      selectNewConnectedAccount(prevAccountsRef.current, accounts);
    }
    prevAccountsRef.current = accounts;
  }, [accounts]);
}
